import { useCallback, useRef, useState, useEffect } from "react";
import type { PropertySelectionRow, RealEstateSearchResult } from "../types";
import { updatePropertyTags } from "../utils/popup/tags/updatePropertyTags";
import {
  findMapClickProperty,
  findPropertyByIdentifier,
} from "../utils/popup/tags/tagUtils";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";

interface PopupInfo {
  location: string;
  chiban: string;
  prefName: string;
}

interface UsePopupTagHandlersReturn {
  popupInfo: PopupInfo | null;
  currentPopupRowId: number | null;
  setPopupInfo: React.Dispatch<React.SetStateAction<PopupInfo | null>>;
  setCurrentPopupRowId: React.Dispatch<React.SetStateAction<number | null>>;
  handleTagsChange: (tags: string[]) => void;
  handleBluePinTagsChange: (
    tags: string[],
    location: string,
    chiban: string,
    prefName: string
  ) => void;
  handleRequestedPicturesTagsChange: (
    tags: string[],
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  handleRequestedPicturesRemarksChange: (
    remarks: string,
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  handleRequestedPicturesAdditionalFieldsChange: (
    additionalFields: string,
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  handleMapClickRemarksChange: (
    remarks: string,
    location: string,
    chiban: string,
    prefName: string
  ) => void;
  handleMapClickAdditionalFieldsChange: (
    additionalFields: string,
    location: string,
    chiban: string,
    prefName: string
  ) => void;
  handleRealEstateSearchRemarksChange: (index: number, remarks: string) => void;
  handleRealEstateSearchAdditionalFieldsChange: (
    index: number,
    additionalFields: string
  ) => void;
  handleReceptionBookTagsChange: (rowId: number, tags: string[]) => void;
  handleReceptionBookRemarksChange: (rowId: number, remarks: string) => void;
  handleReceptionBookAdditionalFieldsChange: (
    rowId: number,
    additionalFields: string
  ) => void;
  handleMapClickTagsUpdate: () => string[];
  handleMapClickRemarksUpdate: () => string;
  handleMapClickAdditionalFieldsUpdate: () => string;
  resetPopupTags: (
    location?: string,
    chiban?: string,
    prefName?: string
  ) => void;
}

interface UsePopupTagHandlersProps {
  selectedProperties: PropertySelectionRow[];
  setSelectedProperties: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  realEstateSearchResult?: RealEstateSearchResult[];
  setRealEstateSearchResult?: React.Dispatch<
    React.SetStateAction<RealEstateSearchResult[]>
  >;
}

export const usePopupTagHandlers = ({
  selectedProperties,
  setSelectedProperties,
  realEstateSearchResult,
  setRealEstateSearchResult,
}: UsePopupTagHandlersProps): UsePopupTagHandlersReturn => {
  // 現在のポップアップの識別情報を管理
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);

  // 現在のポップアップに対応する行のIDを管理
  const [currentPopupRowId, setCurrentPopupRowId] = useState<number | null>(
    null
  );

  // 最新の値を保持するためのref
  const currentPopupRowIdRef = useRef<number | null>(null);

  // currentPopupRowIdの変化をrefに同期
  useEffect(() => {
    currentPopupRowIdRef.current = currentPopupRowId;
  }, [currentPopupRowId]);

  // 汎用的なタグ変更ハンドラー（現在は主に検索結果のピンで使用）
  // 注意：青ピンと赤ピンでcurrentPopupRowIdが競合する問題があったため、
  // それぞれ専用のハンドラー（handleBluePinTagsChange、handleOwnerPlotTagsChange）を使用
  const handleTagsChange = useCallback(
    (tags: string[]): void => {
      // currentPopupIdentifierを使って正しい行を特定
      if (popupInfo) {
        setSelectedProperties((prevProperties) => {
          const targetRow = findPropertyByIdentifier(prevProperties, popupInfo);

          if (!targetRow) {
            return prevProperties;
          }

          return updatePropertyTags(prevProperties, targetRow.id, tags);
        });
      } else {
        // 従来の方法（新規作成時など）
        setSelectedProperties((prevProperties) => {
          const targetRowId = currentPopupRowIdRef.current;

          if (targetRowId === null) {
            return prevProperties;
          }

          return updatePropertyTags(prevProperties, targetRowId, tags);
        });
      }
    },
    [popupInfo, setSelectedProperties]
  );

  // 青ピン（地図クリック）専用のタグ変更ハンドラー
  // 位置情報で直接対象行を特定することで、currentPopupRowIdの競合を回避
  const handleBluePinTagsChange = useCallback(
    (
      tags: string[],
      location: string,
      chiban: string,
      prefName: string
    ): void => {
      setSelectedProperties((prevProperties) => {
        const targetRow = findMapClickProperty(
          prevProperties,
          location,
          chiban,
          prefName
        );

        if (!targetRow) {
          return prevProperties;
        }

        return updatePropertyTags(prevProperties, targetRow.id, tags);
      });
    },
    [setSelectedProperties]
  );

  // pinTypeと位置情報で行を特定するヘルパー関数
  const findPropertyByPinTypeAndLocation = (
    properties: PropertySelectionRow[],
    pinType: "owner-plot" | "requested-pictures",
    prefName: string,
    location: string,
    chiban: string
  ): PropertySelectionRow | undefined => {
    const normalizedLocation = NYHalfWidthNumberToFillWidthNumber(location);
    const normalizedChiban = NYHalfWidthNumberToFillWidthNumber(chiban);

    return properties.find(
      (row) =>
        row.pinType === pinType &&
        row.prefName === prefName &&
        NYHalfWidthNumberToFillWidthNumber(row.location) ===
          normalizedLocation &&
        NYHalfWidthNumberToFillWidthNumber(row.chibanKaokuNumber) ===
          normalizedChiban
    );
  };

  // 地図クリック専用の備考変更ハンドラー
  // 青ピンは位置情報で行を特定するため、currentPopupRowIdの影響を受けない
  // 注意：location と chiban は半角/全角の正規化が必要
  const handleMapClickRemarksChange = useCallback(
    (
      remarks: string,
      location: string,
      chiban: string,
      prefName: string
    ): void => {
      setSelectedProperties((prevProperties) => {
        const normalizedSearchLocation =
          NYHalfWidthNumberToFillWidthNumber(location);
        const normalizedSearchChiban =
          NYHalfWidthNumberToFillWidthNumber(chiban);

        const targetRow = prevProperties.find(
          (row) =>
            row.pinType === "map-click" &&
            row.prefName === prefName &&
            NYHalfWidthNumberToFillWidthNumber(row.location) ===
              normalizedSearchLocation &&
            NYHalfWidthNumberToFillWidthNumber(row.chibanKaokuNumber) ===
              normalizedSearchChiban
        );

        if (!targetRow) {
          return prevProperties;
        }

        return prevProperties.map((row) =>
          row.id === targetRow.id ? { ...row, remarks } : row
        );
      });
    },
    [setSelectedProperties]
  );

  // 地図クリック専用の追加項目変更ハンドラー
  // 青ピンは位置情報で行を特定するため、currentPopupRowIdの影響を受けない
  // 注意：location と chiban は半角/全角の正規化が必要
  const handleMapClickAdditionalFieldsChange = useCallback(
    (
      additionalFields: string,
      location: string,
      chiban: string,
      prefName: string
    ): void => {
      setSelectedProperties((prevProperties) => {
        const normalizedSearchLocation =
          NYHalfWidthNumberToFillWidthNumber(location);
        const normalizedSearchChiban =
          NYHalfWidthNumberToFillWidthNumber(chiban);

        const targetRow = prevProperties.find(
          (row) =>
            row.pinType === "map-click" &&
            row.prefName === prefName &&
            NYHalfWidthNumberToFillWidthNumber(row.location) ===
              normalizedSearchLocation &&
            NYHalfWidthNumberToFillWidthNumber(row.chibanKaokuNumber) ===
              normalizedSearchChiban
        );

        if (!targetRow) {
          return prevProperties;
        }

        return prevProperties.map((row) =>
          row.id === targetRow.id ? { ...row, additionalFields } : row
        );
      });
    },
    [setSelectedProperties]
  );

  // 不動産検索結果の備考変更ハンドラー
  // selectedPropertiesとrealEstateSearchResultの両方を更新する
  const handleRealEstateSearchRemarksChange = useCallback(
    (index: number, remarks: string): void => {
      if (!realEstateSearchResult || !setRealEstateSearchResult) return;

      const item = realEstateSearchResult[index];
      if (!item) return;

      // realEstateSearchResultを更新
      setRealEstateSearchResult((prevResults) => {
        return prevResults.map((result, idx) => {
          if (idx === index) {
            return { ...result, remarks };
          }
          return result;
        });
      });

      // selectedPropertiesを更新
      setSelectedProperties((prevSelected) => {
        return prevSelected.map((prop) => {
          if (
            prop.pinType === "real-estate-search" &&
            prop.bookType === item.bookType &&
            prop.prefName === item.prefName &&
            prop.location === item.location &&
            prop.chibanKaokuNumber === item.chibanKaokuNumber
          ) {
            return { ...prop, remarks };
          }
          return prop;
        });
      });
    },
    [realEstateSearchResult, setRealEstateSearchResult, setSelectedProperties]
  );

  // 不動産検索結果の追加項目変更ハンドラー
  // selectedPropertiesとrealEstateSearchResultの両方を更新する
  const handleRealEstateSearchAdditionalFieldsChange = useCallback(
    (index: number, additionalFields: string): void => {
      if (!realEstateSearchResult || !setRealEstateSearchResult) return;

      const item = realEstateSearchResult[index];
      if (!item) return;

      // realEstateSearchResultを更新
      setRealEstateSearchResult((prevResults) => {
        return prevResults.map((result, idx) => {
          if (idx === index) {
            return { ...result, additionalFields };
          }
          return result;
        });
      });

      // selectedPropertiesを更新
      setSelectedProperties((prevSelected) => {
        return prevSelected.map((prop) => {
          if (
            prop.pinType === "real-estate-search" &&
            prop.bookType === item.bookType &&
            prop.prefName === item.prefName &&
            prop.location === item.location &&
            prop.chibanKaokuNumber === item.chibanKaokuNumber
          ) {
            return { ...prop, additionalFields };
          }
          return prop;
        });
      });
    },
    [realEstateSearchResult, setRealEstateSearchResult, setSelectedProperties]
  );

  // RequestedPicturesPlotLayer専用のタグ変更ハンドラー
  // 位置情報で行を特定するため、currentPopupRowIdの影響を受けない
  const handleRequestedPicturesTagsChange = useCallback(
    (
      tags: string[],
      prefName: string,
      location: string,
      chiban: string
    ): void => {
      setSelectedProperties((prevProperties) => {
        const targetRow = findPropertyByPinTypeAndLocation(
          prevProperties,
          "requested-pictures",
          prefName,
          location,
          chiban
        );

        if (!targetRow) {
          return prevProperties;
        }

        return updatePropertyTags(prevProperties, targetRow.id, tags);
      });
    },
    [setSelectedProperties]
  );

  // RequestedPicturesPlotLayer専用の備考変更ハンドラー
  // 位置情報で行を特定するため、currentPopupRowIdの影響を受けない
  const handleRequestedPicturesRemarksChange = useCallback(
    (
      remarks: string,
      prefName: string,
      location: string,
      chiban: string
    ): void => {
      setSelectedProperties((prevProperties) => {
        const targetRow = findPropertyByPinTypeAndLocation(
          prevProperties,
          "requested-pictures",
          prefName,
          location,
          chiban
        );

        if (!targetRow) {
          return prevProperties;
        }

        return prevProperties.map((row) =>
          row.id === targetRow.id ? { ...row, remarks } : row
        );
      });
    },
    [setSelectedProperties]
  );

  // RequestedPicturesPlotLayer専用の追加項目変更ハンドラー
  // 位置情報で行を特定するため、currentPopupRowIdの影響を受けない
  const handleRequestedPicturesAdditionalFieldsChange = useCallback(
    (
      additionalFields: string,
      prefName: string,
      location: string,
      chiban: string
    ): void => {
      setSelectedProperties((prevProperties) => {
        const targetRow = findPropertyByPinTypeAndLocation(
          prevProperties,
          "requested-pictures",
          prefName,
          location,
          chiban
        );

        if (!targetRow) {
          return prevProperties;
        }

        return prevProperties.map((row) =>
          row.id === targetRow.id ? { ...row, additionalFields } : row
        );
      });
    },
    [setSelectedProperties]
  );

  // 受付帳（reception-book）専用のタグ変更ハンドラー
  // rowIdで行を特定する
  const handleReceptionBookTagsChange = useCallback(
    (rowId: number, tags: string[]): void => {
      setSelectedProperties((prevProperties) => {
        return updatePropertyTags(prevProperties, rowId, tags);
      });
    },
    [setSelectedProperties]
  );

  // 受付帳（reception-book）専用の備考変更ハンドラー
  // rowIdで行を特定する
  const handleReceptionBookRemarksChange = useCallback(
    (rowId: number, remarks: string): void => {
      setSelectedProperties((prevProperties) => {
        return prevProperties.map((row) =>
          row.id === rowId ? { ...row, remarks } : row
        );
      });
    },
    [setSelectedProperties]
  );

  // 受付帳（reception-book）専用の追加項目変更ハンドラー
  // rowIdで行を特定する
  const handleReceptionBookAdditionalFieldsChange = useCallback(
    (rowId: number, additionalFields: string): void => {
      setSelectedProperties((prevProperties) => {
        return prevProperties.map((row) =>
          row.id === rowId ? { ...row, additionalFields } : row
        );
      });
    },
    [setSelectedProperties]
  );

  // 青ピン（地図クリック）のポップアップが開かれた際に最新のタグを取得する関数
  const handleMapClickTagsUpdate = useCallback((): string[] => {
    // currentPopupIdentifierがない場合は早期リターン
    if (!popupInfo) {
      return [];
    }

    // 青ピンの場合、markerStateとselectedPropertiesが常に同期しているため、
    // currentPopupIdentifierで対象行を特定できる
    const targetRow = findPropertyByIdentifier(selectedProperties, popupInfo);

    return targetRow?.tags ?? [];
  }, [popupInfo, selectedProperties]);

  // 青ピン（地図クリック）のポップアップが開かれた際に最新の備考を取得する関数
  const handleMapClickRemarksUpdate = useCallback((): string => {
    if (!popupInfo) {
      return "";
    }

    const targetRow = findPropertyByIdentifier(selectedProperties, popupInfo);
    return targetRow?.remarks ?? "";
  }, [popupInfo, selectedProperties]);

  // 青ピン（地図クリック）のポップアップが開かれた際に最新の追加項目を取得する関数
  const handleMapClickAdditionalFieldsUpdate = useCallback((): string => {
    if (!popupInfo) {
      return "";
    }

    const targetRow = findPropertyByIdentifier(selectedProperties, popupInfo);
    return targetRow?.additionalFields ?? "";
  }, [popupInfo, selectedProperties]);

  // ポップアップ表示時にタグをリセットし、識別情報を設定する関数
  const resetPopupTags = useCallback(
    (location = "", chiban = "", prefName = ""): void => {
      setPopupInfo(
        location && chiban && prefName ? { location, chiban, prefName } : null
      );
      setCurrentPopupRowId(null);
    },
    []
  );

  return {
    popupInfo,
    currentPopupRowId,
    setPopupInfo,
    setCurrentPopupRowId,
    handleTagsChange,
    handleBluePinTagsChange,
    handleRequestedPicturesTagsChange,
    handleRequestedPicturesRemarksChange,
    handleRequestedPicturesAdditionalFieldsChange,
    handleMapClickRemarksChange,
    handleMapClickAdditionalFieldsChange,
    handleRealEstateSearchRemarksChange,
    handleRealEstateSearchAdditionalFieldsChange,
    handleReceptionBookTagsChange,
    handleReceptionBookRemarksChange,
    handleReceptionBookAdditionalFieldsChange,
    handleMapClickTagsUpdate,
    handleMapClickRemarksUpdate,
    handleMapClickAdditionalFieldsUpdate,
    resetPopupTags,
  };
};
